<template>
  <div class="bg-[#ABBCF4] w-48 rounded-3xl p-3 overflow-hidden relative" style="height: 214px;">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <h2 class="text-base font-bold text-white">Hotspots</h2>
      </div>
      <div class="flex space-x-1">
        <button
          @click="prevSlide"
          class="bg-white bg-opacity-20 text-white p-1 rounded-full hover:bg-opacity-30 transition-all"
          title="Previous"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          @click="nextSlide"
          class="bg-white bg-opacity-20 text-white p-1 rounded-full hover:bg-opacity-30 transition-all"
          title="Next"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Carousel Container -->
    <div class="relative h-40 rounded-2xl overflow-hidden cursor-pointer" @click="openSlide">
      <!-- Slides -->
      <div 
        class="flex transition-transform duration-500 ease-in-out h-full"
        :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
      >
        <div
          v-for="(slide, index) in slides"
          :key="index"
          class="w-full h-full flex-shrink-0 relative"
        >
          <img
            :src="slide.image"
            :alt="slide.title"
            class="w-full h-full object-cover"
            @error="handleImageError(index)"
          />
          <!-- Overlay with title and shadow -->
          <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 shadow-lg">
            <h3 class="text-white text-sm font-semibold drop-shadow-md">{{ slide.title }}</h3>
            <p class="text-white/90 text-xs drop-shadow-sm">{{ slide.subtitle }}</p>
          </div>
        </div>
      </div>

      <!-- Slide Indicators -->
      <div class="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1">
        <button
          v-for="(slide, index) in slides"
          :key="index"
          @click.stop="goToSlide(index)"
          class="w-1 h-[2px] rounded-full transition-all"
          :class="currentSlide === index ? 'bg-white/50' : 'bg-white/20'"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HotspotsCarousel',
  data() {
    return {
      currentSlide: 0,
      autoSlideInterval: null,
      slides: [
        {
          image: 'https://seashellhotels.net/wp-content/uploads/2024/12/Drone-view-of-Andaman-Islands.png',
          title: 'Andaman Islands',
          subtitle: 'Pristine beaches.'
        },
        {
          image: 'https://www.tripplannersindia.com/assets/images/page/Srinagar_Sonmarg_Pahalgam_Gulmarg_Tour_Package_for_5_Days_4_Nights_(2).webp',
          title: 'Kashmir Valley',
          subtitle: 'Paradise on Earth'
        },
        {
          image: 'https://imgcld.yatra.com/ytimages/image/upload/v1517480778/AdvNation/ANN_DES95/ann_top_Ladakh_buV00Q.jpg',
          title: 'Ladakh',
          subtitle: 'Land of high passes'
        },
        {
          image: 'https://www.sotc.in/blog/wp-content/uploads/2023/08/Beautiful-Hills-Of-Meghalaya.jpg',
          title: 'Meghalaya',
          subtitle: 'Abode of clouds'
        },
        {
          image: 'https://blog.weekendthrill.com/wp-content/uploads/2016/11/112116_1206_15Placestov2.png',
          title: 'Shimla',
          subtitle: 'Snowy mountain retreat'
        }
      ]
    };
  },
  mounted() {
    this.startAutoSlide();
  },
  beforeUnmount() {
    this.stopAutoSlide();
  },
  methods: {
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentSlide = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    },
    goToSlide(index) {
      this.currentSlide = index;
    },
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 4000); // Change slide every 4 seconds
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
        this.autoSlideInterval = null;
      }
    },
    openSlide() {
      const currentSlideData = this.slides[this.currentSlide];
      // Emit event or handle slide click
      this.$emit('hotspot-selected', currentSlideData);
      // For now, just log the selected hotspot
      console.log('Selected hotspot:', currentSlideData.title);
    },
    handleImageError(index) {
      // Fallback image if the original fails to load
      this.slides[index].image = 'https://via.placeholder.com/300x200/ABBCF4/ffffff?text=Hotspot';
    }
  }
};
</script>

<style scoped>
/* Smooth transitions */
.transition-transform {
  transition: transform 0.5s ease-in-out;
}

/* Hover effects */
.cursor-pointer:hover {
  transform: scale(1.02);
  transition: transform 0.2s ease;
}

/* Custom scrollbar if needed */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}
</style>
